import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Auth store — guarda APENAS o usuário logado em sessão.
 *
 * P1-Auth (ADR 0018, 2026-05-19): tokens migraram para cookies httpOnly
 * gerenciados pelo backend. O store guarda só `usuario` (necessário para
 * `<PrivateRoute>` e para exibir nome/empresa no header sem precisar
 * chamar `/auth/me` em cada render). O cookie é o único portador de
 * autenticação efetiva; o `usuario` no localStorage é estado UI e não
 * dá poder a um atacante que conseguir lê-lo.
 *
 * O `_hasHydrated` é gerenciado pelo middleware persist e exposto para o
 * `<HydrationGate>` evitar flash de "não logado" no primeiro render.
 */

export interface UsuarioSession {
  id: string;
  nome: string;
  email: string;
  papeis: string[];
  empresa: {
    id: string;
    razaoSocial: string;
  };
}

interface AuthState {
  usuario: UsuarioSession | null;
  _hasHydrated: boolean;

  login: (params: { usuario: UsuarioSession }) => void;
  /** Atualiza campos do usuário em sessão (ex: nome alterado em /perfil). */
  setUsuario: (patch: Partial<UsuarioSession>) => void;
  logout: () => void;
  setHasHydrated: (value: boolean) => void;
}

const STORAGE_KEY = 'gestao-projetos:auth';

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      usuario: null,
      _hasHydrated: false,
      login: ({ usuario }) => set({ usuario }),
      setUsuario: (patch) =>
        set((state) => (state.usuario ? { usuario: { ...state.usuario, ...patch } } : state)),
      logout: () => set({ usuario: null }),
      setHasHydrated: (value) => set({ _hasHydrated: value }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        usuario: state.usuario,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    },
  ),
);
